import { CommentsApi, type EntryComment } from "@/api/comments/CommentsApi";
import { queryKeys } from "@/api/queryKeys";
import { useAuth } from "@/auth/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useCaseComments(caseId: string | undefined, enabled: boolean) {
  const q = useQuery({
    queryKey: queryKeys.comments.forCase(caseId ?? ""),
    queryFn: () => CommentsApi.listForCase(caseId!),
    enabled: !!caseId && enabled,
  });
  return {
    comments: (q.data ?? []) as EntryComment[],
    loading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  };
}

export function useMentionableMembers(caseId: string | undefined, enabled: boolean) {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: queryKeys.comments.mentionable(caseId ?? ""),
    queryFn: () => CommentsApi.listMentionable(caseId!),
    enabled: !!user?.id && !!caseId && enabled,
    staleTime: 30_000,
  });
  return { members: q.data ?? [], loading: q.isLoading };
}

export function useAddEntryComment(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      entryId: string;
      body: string;
      mentionedProfileIds?: string[];
    }) => CommentsApi.add(input),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.comments.forCase(caseId ?? ""),
      });
    },
  });
}

export function useEditEntryComment(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      commentId: string;
      body: string;
      mentionedProfileIds?: string[];
    }) => CommentsApi.edit(input),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.comments.forCase(caseId ?? ""),
      });
    },
  });
}

export function useWithdrawEntryComment(caseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => CommentsApi.withdraw(commentId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.comments.forCase(caseId ?? ""),
      });
    },
  });
}
